import { useEffect, useState } from 'react';
import api from '../api/client';
import { gateway } from '../api/gateway';
import { storage } from '../utils/storage';
import { useAuthContext } from '../context/AuthContext';

const buildFallbackHealth = (base) => ({
  status: base?.status || 'UNKNOWN',
  infrastructure: {
    status: base?.status || 'UNKNOWN',
    service: base?.service || 'api-gateway',
    maintenance: !!base?.maintenance,
    servicesUp: 0,
    servicesDown: 0,
    liveServices: 0
  },
  metrics: {
    latency: 0,
    requests: 0,
    routes: 0,
    errors: 0
  },
  services: []
});

export const useHealth = () => {
  const [health, setHealth] = useState(buildFallbackHealth());
  const { user } = useAuthContext();
  const role = String(user?.role || '').toLowerCase();
  const isAdmin = role === 'admin' || role === 'super_admin';

  useEffect(() => {
    let cancelled = false;

    const loadHealth = async () => {
      try {
        const gatewayHealth = await gateway.health();
        const hasAccessToken = storage.hasUsableAccessToken();

        if (!hasAccessToken) {
          if (cancelled) return;
          setHealth(buildFallbackHealth(gatewayHealth));
          return;
        }

        // Admin-only deep status. Skipping for non-admins keeps the
        // browser console clean (avoids 403s on every /metrics/live and
        // /status/services poll for ordinary users).
        if (!isAdmin) {
          if (cancelled) return;
          setHealth(buildFallbackHealth(gatewayHealth));
          return;
        }

        const probeStart = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        const [serviceStatusResult, metricsResult] = await Promise.allSettled([
          gateway.serviceStatus(),
          gateway.metricsLive()
        ]);
        const probeEnd = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

        if (cancelled) return;

        const serviceStatus = serviceStatusResult.status === 'fulfilled' ? serviceStatusResult.value : {};
        const metrics = metricsResult.status === 'fulfilled' ? metricsResult.value : {};
        const services = Array.isArray(serviceStatus?.services) ? serviceStatus.services : [];
        const servicesUp = services.filter((service) => service?.status === 'UP').length;
        const servicesDown = services.filter((service) => service?.status !== 'UP').length;
        // Real latency: a backend-reported figure when present, otherwise the
        // client-observed round-trip of the status probe. (Previously this was
        // fabricated as servicesDown * 250, which misrepresented health.)
        const latency = Math.round(
          Number(metrics?.latencyMs ?? metrics?.avgLatencyMs) || (probeEnd - probeStart)
        );
        const overallStatus =
          gatewayHealth?.maintenance
            ? 'MAINTENANCE'
            : servicesDown > 0
              ? 'DEGRADED'
              : (gatewayHealth?.status || 'UP');

        setHealth({
          status: overallStatus,
          infrastructure: {
            status: overallStatus,
            service: gatewayHealth?.service || 'api-gateway',
            maintenance: !!gatewayHealth?.maintenance,
            servicesUp,
            servicesDown,
            liveServices: services.length
          },
          metrics: {
            latency,
            requests: metrics?.throughput5m || 0,
            routes: metrics?.routes || 0,
            errors: metrics?.errors5m || 0
          },
          services
        });
      } catch {
        try {
          const res = await api.get('/health');
          if (cancelled) return;
          setHealth(buildFallbackHealth(res.data?.data));
        } catch {
          if (cancelled) return;
          setHealth(buildFallbackHealth());
        }
      }
    };

    loadHealth();
    const interval = window.setInterval(loadHealth, 30000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isAdmin]);

  return health;
};
